/* eslint-disable no-loop-func */
import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { flowRight } from 'lodash/fp';

import { accessEnvironment, setNestedValue } from 'utils/system';

import { withAppContext } from '../AppContext';
import NavMenuNode from './NavMenuNode';
import { getConfig } from '../../routing';

function buildMenu(parent, config, context) {
    const {
        name,
        label,
        url = '',
        access = '',
        icon = null,
        isMenu = false,
        children = [],
        envs = null,
        role,
        expanded,
    } = config;

    const node = {
        parent,
        name,
        label,
        icon,
        access,
        hasAccess: false,
        selected: false,
        active: false,
        visible: false,
        url,
        envs,
        role,
        expanded,
    };
    node.deep = (parent && parent.deep + 1) || 0;
    node.children = children
        .map(child => buildMenu(node, child, context))
        .filter(child => context.nodeFilter(child));
    node.url = fixNodeUrl(node);
    node.path = fixNodePath(node);
    if (context.isNodeSelected(node)) {
        prepareNodeState(node);
    }

    node.visible = isMenu;
    return node;
}

function fixNodeUrl(node) {
    if (node.role === 'collapse') {
        return null;
    }
    let url = node.url;
    while (!url) {
        if (node.children.length) {
            node = node.children[0];
            url = node.url;
            continue;
        }
        break;
    }
    return url;
}
function fixNodePath(node) {
    const buff = [];
    while (node && node.name) {
        if (!isNodeRoot(node)) {
            buff.push(node.name);
        }
        node = node.parent;
    }
    return `/${buff.reverse().join('/')}`;
}

const isNodeRoot = node => node.name === 'root';
const prepareNodeState = node => {
    node.active = node.selected = true;
    node = node.parent;
    while (node) {
        node.active = true;
        node.selected = false;
        node = node.parent;
    }
};

const isNodeSelected = pathname => node => {
    return pathname.indexOf(node.path) === 0;
};

const hasNodeAccess = ({ currentUser }) => ({ access: { permission } }) => {
    return permission ? currentUser.canView(permission) : true;
};

const hasEnvironment = ({ settings: { environment } }) => ({ envs }) =>
    accessEnvironment(envs, environment);

class NavMenu extends React.PureComponent {
    state = {
        menu: {},
    };
    updateMenu(config) {
        const {
            location: { pathname },
            app,
            onUpdateMenu,
        } = this.props;
        const menu = buildMenu(null, config, {
            isNodeSelected: isNodeSelected(pathname),
            nodeFilter: data =>
                hasNodeAccess(app)(data) && hasEnvironment(app)(data),
        });
        onUpdateMenu(menu, config);
    }
    componentDidMount() {
        this.updateMenu(this.props.config);
    }
    componentDidUpdate(prevProps, prevState) {
        const {
            location: { pathname },
            config,
            app,
        } = this.props;
        const userChanged =
            app.currentUser?.email !== prevProps.app?.currentUser?.email;
        if (
            userChanged ||
            (pathname !== prevProps.location.pathname &&
                this.props.drawerOpened)
        ) {
            this.updateMenu(config);
        }
    }

    getObjectPath = node => {
        const path = [];
        while (node.parent) {
            const indexInParent = node.parent.children.findIndex(
                item => item === node
            );
            path.unshift(indexInParent, 'children');
            node = node.parent;
        }
        path.pop();
        path.unshift('children');
        return path.join('.');
    };

    handleActionMenuOpen = node => {
        let { menu, config, onUpdateMenu } = this.props;
        if (node.role === 'collapse') {
            const nodePath = this.getObjectPath(node) + '.expanded';
            setNestedValue(menu, nodePath, !node.expanded);
            setNestedValue(config, nodePath, node.expanded);
        } else {
            config = getConfig();
        }
        onUpdateMenu(menu, config);
        this.setState((state, props) => {
            this.props.onAccountMenuClick();
        });
    };
    render() {
        const { menu } = this.props;

        if (menu.children) {
            return menu.children
                .filter(child => child.visible)
                .map(child => this.renderMenu(child));
        }
        return null;
    }
    renderMenu(node) {
        if (isNodeRoot(node) || !node.visible) {
            return null;
        }
        return (
            <NavMenuNode
                key={node.url}
                node={node}
                onClick={item => this.handleActionMenuOpen(item || node)}
            />
        );
    }
}

NavMenu.propTypes = {
    location: PropTypes.object.isRequired,
    app: PropTypes.object.isRequired,
};

export default flowRight(withRouter, withAppContext)(NavMenu);
